import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { StorageKey, readStored, writeStored } from "../lib/storage";
import { ThemeContext, themeOrder, type ThemeMode, type ThemeState } from "./theme-context";

const DARK_QUERY = "(prefers-color-scheme: dark)";

function isMode(value: string | null): value is ThemeMode {
  return value === "light" || value === "dark" || value === "system";
}

function forcedMode(): ThemeMode | null {
  if (typeof window === "undefined") return null;
  const value = new URLSearchParams(window.location.search).get("theme");
  return value === "light" || value === "dark" ? value : null;
}

function initialMode(): ThemeMode {
  const forced = forcedMode();
  if (forced !== null) return forced;
  const stored = readStored(StorageKey.theme);
  return isMode(stored) ? stored : "system";
}

function prefersDark(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(DARK_QUERY).matches;
}

export function ThemeProvider({ children }: { children: ReactNode }): ReactNode {
  const [mode, setModeState] = useState<ThemeMode>(initialMode);
  const [systemDark, setSystemDark] = useState<boolean>(prefersDark);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const query = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent): void => {
      setSystemDark(event.matches);
    };
    query.addEventListener("change", onChange);
    return () => {
      query.removeEventListener("change", onChange);
    };
  }, []);

  const resolved: "light" | "dark" = mode === "system" ? (systemDark ? "dark" : "light") : mode;

  // An explicit choice stamps `data-theme`; "system" removes it so the stylesheet's
  // `prefers-color-scheme` block takes over.
  useEffect(() => {
    const root = document.documentElement;
    if (mode === "system") {
      root.removeAttribute("data-theme");
    } else {
      root.setAttribute("data-theme", mode);
    }
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    writeStored(StorageKey.theme, next);
  }, []);

  const cycle = useCallback(() => {
    setModeState((current) => {
      const index = themeOrder.indexOf(current);
      const next = themeOrder[(index + 1) % themeOrder.length] ?? "system";
      writeStored(StorageKey.theme, next);
      return next;
    });
  }, []);

  const value = useMemo<ThemeState>(
    () => ({ mode, resolved, setMode, cycle }),
    [cycle, mode, resolved, setMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
